import moment from 'moment';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { io, Socket } from 'socket.io-client';

import { addNewActiveUser, removeActiveUser } from '../context/appReducer';
import {
  addNewMessage,
  updateMessageRoomList,
  updateOrDeleteSelectedMessage,
  updateReplyState,
} from '../context/communityMessageReducer';
import { store } from '../context/store';
// import {
//   addNewActiveUser,
//   removeActiveUser,
// } from '../context/store/appReducer';
// import {
//   addNewMessage,
//   deleteMessage,
// } from '../context/thunks/communityMessagesThunk';
import {
  SOCKET_ACTION_EVENTS_MAP,
  SOCKET_BROADCAST_EVENTS,
  SOCKET_BROADCAST_EVENTS_MAP,
} from '../types/socketTypes';
import config from './config';

export class SocketAPI {
  private static instance: Socket | null = null;

  private static token: string | null = null;

  static initialize = (token: string) => {
    if (this.instance) return this.instance; // Prevent multiple instances

    console.log('🔄 Initializing new socket connection...');
    this.token = token;

    this.instance = io(config.COMMUNITY_SOCKET_ENDPOINT, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true, // Enable reconnection
      reconnectionAttempts: 10,
      reconnectionDelay: 3000, // Try reconnecting every 3s
      reconnectionDelayMax: 10000,
    });

    this.instance.on('connect', () => {
      console.log('✅ Socket Connected:', this.instance?.id);

      const { dispatch } = store;

      if (this.instance) {
        this.instance.off(SOCKET_BROADCAST_EVENTS.USER_JOINED);
        this.instance.on(SOCKET_BROADCAST_EVENTS.USER_JOINED, (event) => {
          dispatch(addNewActiveUser(event.payload.user));
        });

        this.instance.off(SOCKET_BROADCAST_EVENTS.USER_LEFT);
        this.instance.on(SOCKET_BROADCAST_EVENTS.USER_LEFT, (event) => {
          dispatch(removeActiveUser(event.payload.user));
        });

        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_CREATED);
        this.instance.on(
          SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_CREATED,
          (resp) => {
            if (resp.payload.message.threadRoot) {
              // If the message is a reply to a thread
              const { communityMessage } = store.getState();
              const { replyState, replayMessageData } = communityMessage;
              const chatState = communityMessage?.chatsState?.chats || [];
              const threadRootMessage = chatState.find(
                (chat) => chat._id === resp.payload.message.threadRoot,
              );

              if (threadRootMessage) {
                const currentThreadMetadata =
                  threadRootMessage?.threadRootMetadata || {
                    totalReplies: 0,
                    lastRepliedAt: moment().toISOString(),
                    lastReply: resp.payload.message._id,
                  };
                const currentReplyCount =
                  currentThreadMetadata?.totalReplies || 0;
                const newReplyCount = currentReplyCount + 1;

                dispatch(
                  updateOrDeleteSelectedMessage(
                    {
                      ...threadRootMessage,
                      threadRootMetadata: {
                        ...currentThreadMetadata,
                        totalReplies: newReplyCount,
                      },
                    },
                    false,
                    () => {},
                  ),
                );

                // If this thread root matches the current reply data ID, add the message to reply state
                if (
                  replayMessageData?._id === resp.payload.message.threadRoot
                ) {
                  dispatch(
                    updateReplyState({
                      replies: [...replyState?.replies, resp.payload.message],
                    }),
                  );
                }
              }
            } else {
              dispatch(
                addNewMessage({ ...resp.payload.message, isNewMessage: true }),
              );
            }
          },
        );
        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_EDITED);
        this.instance.on(
          SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_EDITED,
          (resp) => {
            dispatch(updateOrDeleteSelectedMessage(resp.payload.message));
          },
        );

        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_REACTED);
        this.instance.on(
          SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_REACTED,
          (resp) => {
            const { communityMessage } = store.getState();
            const { replyState, replayMessageData, chatsState } =
              communityMessage;

            // Check if message exists in chat state
            const messageInChatState = chatsState?.chats?.find(
              (chat) => chat._id === resp.payload.message._id,
            );

            // Check if message exists in reply state
            const messageInReplyState = replyState?.replies?.find(
              (reply) => reply._id === resp.payload.message._id,
            );

            if (messageInChatState) {
              // Update message in chat state
              dispatch(updateOrDeleteSelectedMessage(resp.payload.message));

              // If this message is in the current thread being viewed, also update it in reply state
              if (replayMessageData?._id === messageInChatState.threadRoot) {
                const updatedReplies = replyState?.replies?.map((reply) =>
                  reply._id === resp.payload.message._id
                    ? resp.payload.message
                    : reply,
                );
                dispatch(updateReplyState({ replies: updatedReplies }));
              }
            } else if (messageInReplyState) {
              // Update message in reply state
              const updatedReplies = replyState?.replies?.map((reply) =>
                reply._id === resp.payload.message._id
                  ? resp.payload.message
                  : reply,
              );
              dispatch(updateReplyState({ replies: updatedReplies }));
            }
          },
        );

        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_DELETED);
        this.instance.on(
          SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_DELETED,
          (resp) => {
            const { communityMessage } = store.getState();
            const { replyState, replayMessageData, chatsState } =
              communityMessage;

            // Check if message exists in chat state
            const messageInChatState = chatsState?.chats?.find(
              (chat) => chat._id === resp.payload.message._id,
            );

            // Check if message exists in reply state
            const messageInReplyState = replyState?.replies?.find(
              (reply) => reply._id === resp.payload.message._id,
            );

            if (messageInChatState) {
              // Update message in chat state
              dispatch(
                updateOrDeleteSelectedMessage(resp.payload.message, true),
              );

              // If this message is in the current thread being viewed, also update it in reply state
              if (replayMessageData?._id === messageInChatState.threadRoot) {
                const updatedReplies = replyState?.replies?.filter(
                  (reply) => reply._id !== resp.payload.message._id,
                );
                dispatch(updateReplyState({ replies: updatedReplies }));
              }
            } else if (messageInReplyState) {
              // Update message in reply state
              const updatedReplies = replyState?.replies?.filter(
                (reply) => reply._id !== resp.payload.message._id,
              );
              dispatch(updateReplyState({ replies: updatedReplies }));
            }
          },
        );

        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_POLL_VOTED);
        this.instance.on(
          SOCKET_BROADCAST_EVENTS.ROOM_MESSAGE_POLL_VOTED,
          (resp) => {
            const { communityMessage } = store.getState();
            const { replyState, replayMessageData, chatsState } =
              communityMessage;

            // Check if message exists in chat state
            const messageInChatState = chatsState?.chats?.find(
              (chat) => chat._id === resp.payload.message._id,
            );

            // Check if message exists in reply state
            const messageInReplyState = replyState?.replies?.find(
              (reply) => reply._id === resp.payload.message._id,
            );

            if (messageInChatState) {
              // Update message in chat state
              dispatch(updateOrDeleteSelectedMessage(resp.payload.message));

              // If this message is in the current thread being viewed, also update it in reply state
              if (replayMessageData?._id === messageInChatState.threadRoot) {
                const updatedReplies = replyState?.replies?.map((reply) =>
                  reply._id === resp.payload.message._id
                    ? resp.payload.message
                    : reply,
                );
                dispatch(updateReplyState({ replies: updatedReplies }));
              }
            } else if (messageInReplyState) {
              // Update message in reply state
              const updatedReplies = replyState?.replies?.map((reply) =>
                reply._id === resp.payload.message._id
                  ? resp.payload.message
                  : reply,
              );
              dispatch(updateReplyState({ replies: updatedReplies }));
            }
          },
        );
        this.instance.off(SOCKET_BROADCAST_EVENTS.ROOM_NEW);
        this.instance.on(SOCKET_BROADCAST_EVENTS.ROOM_NEW, (event) => {
          dispatch(
            updateMessageRoomList({ room: event.payload.room, addToTop: true }),
          );
        });
      }
    });

    this.instance.on('disconnect', (reason) => {
      console.warn('⚠️ Socket Disconnected:', reason);
      if (
        reason === 'io server disconnect' ||
        reason === 'io client disconnect' ||
        reason === 'ping timeout' ||
        reason === 'transport close' ||
        reason === 'transport error'
      ) {
        this.reconnect(); // Handle manual reconnection if disconnected by the server
      }
    });

    this.instance.on('connect_error', (err) => {
      console.error('🚨 Socket Error:', err);
      if (this.instance) {
        this.instance.io.opts.transports = ['polling', 'websocket'];
      }
    });

    this.instance?.on('exception', (error: any) => {
      console.error('🚨 Socket Exception Error:', error);
    });

    return this.instance;
  };

  static getInstance = (): Socket => {
    if (!this.instance) {
      throw new Error(
        '❌ Socket instance not initialized. Call initialize first.',
      );
    }
    return this.instance;
  };

  static reconnect() {
    if (!this.token) {
      console.error('❌ No token found, cannot reconnect.');
      return;
    }
    console.log('🔄 Reconnecting socket...');
    this.disconnect(); // Close existing connection before reconnecting
    this.initialize(this.token);
  }

  static disconnect() {
    if (this.instance) {
      console.log('🔌 Disconnecting socket...');
      this.instance.disconnect();
      this.instance.close();
      this.instance = null;
    }
  }

  static on<T extends keyof SOCKET_BROADCAST_EVENTS_MAP>(
    event: T,
    callback: (data: SOCKET_BROADCAST_EVENTS_MAP[T]) => void,
  ) {
    this.getInstance().on(event, callback as any);
  }

  static emit<T extends keyof SOCKET_ACTION_EVENTS_MAP>(
    event: T,
    data: SOCKET_ACTION_EVENTS_MAP[T],
    callback?: (response: any) => void,
  ) {
    if (!this.instance || !this.instance.connected) {
      console.error('❌ Socket not connected. Cannot emit event:', event);
      callback?.({
        status: 'error',
        message: 'Socket not connected, please refresh the page',
      });
      return;
    }

    if (callback) {
      this.getInstance().emit(event, data, callback);
    } else {
      this.getInstance().emit(event, data);
    }
  }
}
